import { homeworkPathPrefix } from '../CoursePortal/constants'

export const STATUS = {
  UNSUBMITTED: '1',
  SUBMITTED: '2',
  EVALUATED: '3',
  REJECTED: '4',
}

export function normalizeStatus(status) {
  if (status === '已提交') return STATUS.SUBMITTED
  return String(status || STATUS.UNSUBMITTED)
}

export function statusMeta(status, t) {
  const key = normalizeStatus(status)
  return ({
    [STATUS.UNSUBMITTED]: { text: t('hw_status_unsubmitted'), tone: 'pending', color: '#8b95a3' },
    [STATUS.SUBMITTED]: { text: t('hw_status_submitted'), tone: 'submitted', color: '#6f9fff' },
    [STATUS.EVALUATED]: { text: t('hw_status_graded'), tone: 'evaluated', color: '#34d399' },
    [STATUS.REJECTED]: { text: t('hw_status_rejected'), tone: 'rejected', color: '#f87171' },
  })[key] || { text: t('hw_status_unknown'), tone: 'unknown', color: '#8b95a3' }
}

export function trainingTypeLabel(typeVal, t) {
  return ({
    engineering: t('hw_training_type_engineering'),
    discussion: t('hw_training_type_discussion'),
    groupchat: t('hw_training_type_groupchat'),
    plaza: t('hw_training_type_plaza', '专项训练'),
    chatlogs: 'AI ChatLogs',
    lab_report: '实验报告',
    youlaber: 'YouLabber 实验',
  })[typeVal] || t('hw_training_type_engineering')
}

export function scenarioName(homework, t) {
  const type = homework?.trainingType || 'engineering'
  if (type === 'engineering') return homework.processRemarks || t('hw_unnamed_flow')
  if (type === 'discussion') return t('hw_training_type_discussion')
  if (type === 'groupchat') return homework.gcTemplateName || t('hw_unnamed_flow')
  if (type === 'plaza') return homework.plazaAgentName || homework.processRemarks || t('hw_training_type_plaza', '专项训练')
  if (type === 'chatlogs') return 'AI IDE ChatLogs'
  if (type === 'lab_report') return '实验报告'
  if (type === 'youlaber') return homework.youlaberSkillName || 'YouLabber 实验'
  return homework.processRemarks || t('hw_unnamed_flow')
}

export function archiveDisplayTitle(archive, t) {
  if (!archive) return t('hw_unnamed_project')
  const isPlaza = archive.workflowType?.startsWith('plaza') || archive.workflowName?.includes('单智能体')
  if (isPlaza && (archive.agentName || archive.name)) {
    return archive.agentName || archive.name
  }
  return archive.projectName || t('hw_unnamed_project')
}

export function formatDateTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return String(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function deadlineUrgency(deadline) {
  if (!deadline) return 'none'
  const ms = new Date(deadline).getTime() - Date.now()
  if (Number.isNaN(ms)) return 'none'
  if (ms < 0) return 'overdue'
  if (ms < 48 * 3600 * 1000) return 'soon'
  return 'ok'
}

export function homeworkIdOf(hw) {
  return Number(hw?.id || hw?.TrainingAssignmentID || 0)
}

export function courseIdOf(hw) {
  return hw?.CourseID ?? hw?.courseId ?? null
}

export function sortHomeworks(list, sortBy = 'path') {
  const items = [...(list || [])]
  if (sortBy === 'deadline') {
    return items.sort((a, b) => {
      const da = new Date(a.deadline || 0).getTime()
      const db = new Date(b.deadline || 0).getTime()
      if (da !== db) return da - db
      return homeworkIdOf(a) - homeworkIdOf(b)
    })
  }

  const rank = (status) => ({
    [STATUS.UNSUBMITTED]: 0,
    [STATUS.REJECTED]: 1,
    [STATUS.SUBMITTED]: 2,
    [STATUS.EVALUATED]: 3,
  })[normalizeStatus(status)] ?? 9

  if (sortBy === 'status') {
    return items.sort((a, b) => {
      const ra = rank(a.status)
      const rb = rank(b.status)
      if (ra !== rb) return ra - rb
      const da = new Date(a.deadline || 0).getTime()
      const db = new Date(b.deadline || 0).getTime()
      if (da !== db) return da - db
      return homeworkIdOf(a) - homeworkIdOf(b)
    })
  }

  return items.sort((a, b) => {
    const byPath = compareHomeworkPathTitle(a, b)
    if (byPath !== 0) return byPath
    return homeworkIdOf(a) - homeworkIdOf(b)
  })
}

function compareHomeworkPathTitle(a, b) {
  const pa = homeworkPathPrefix(a, a?.timelineUnit)
  const pb = homeworkPathPrefix(b, b?.timelineUnit)
  const aRank = pa ? 0 : 1
  const bRank = pb ? 0 : 1
  if (aRank !== bRank) return aRank - bRank
  const collator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' })
  const byPrefix = collator.compare(pa, pb)
  if (byPrefix !== 0) return byPrefix
  return collator.compare(String(a?.title || a?.Title || ''), String(b?.title || b?.Title || ''))
}

export function filterHomeworks(list, { courseId = 'all', statusFilter = 'all', keyword = '' } = {}) {
  const kw = keyword.trim().toLowerCase()
  return (list || []).filter((hw) => {
    if (courseId !== 'all' && String(courseIdOf(hw)) !== String(courseId)) return false
    const st = normalizeStatus(hw.status)
    if (statusFilter === 'pending' && st !== STATUS.UNSUBMITTED && st !== STATUS.REJECTED) return false
    if (statusFilter === 'submitted' && st !== STATUS.SUBMITTED) return false
    if (statusFilter === 'evaluated' && st !== STATUS.EVALUATED) return false
    if (statusFilter === 'rejected' && st !== STATUS.REJECTED) return false
    if (kw) {
      const title = String(hw.title || hw.Title || '').toLowerCase()
      const course = String(hw.courseName || hw.CourseName || '').toLowerCase()
      const prefix = homeworkPathPrefix(hw, hw.timelineUnit).toLowerCase()
      if (!title.includes(kw) && !course.includes(kw) && !prefix.includes(kw)) return false
    }
    return true
  })
}

export function countByStatus(list) {
  const counts = { all: 0, pending: 0, submitted: 0, evaluated: 0, rejected: 0 }
  ;(list || []).forEach((hw) => {
    counts.all += 1
    const st = normalizeStatus(hw.status)
    if (st === STATUS.UNSUBMITTED) counts.pending += 1
    else if (st === STATUS.REJECTED) {
      counts.pending += 1
      counts.rejected += 1
    } else if (st === STATUS.SUBMITTED) counts.submitted += 1
    else if (st === STATUS.EVALUATED) counts.evaluated += 1
  })
  return counts
}

export function processHtmlContent(content, apiBase) {
  if (!content) return ''
  let processed = content
    .replace(/src="\/api\//g, `src="${apiBase}/api/`)
    .replace(/src="api\//g, `src="${apiBase}/api/`)
    .replace(/src="\.\/api\//g, `src="${apiBase}/api/`)
    .replace(/src="\.\.\/api\//g, `src="${apiBase}/api/`)
    .replace(/src='\/api\//g, `src='${apiBase}/api/`)
    .replace(/src='api\//g, `src='${apiBase}/api/`)

  processed = processed.replace(
    /src="([^"]*\/api\/uploads\/)(?!file\/)([^"]*\.(png|jpg|jpeg|gif|webp|svg))"/gi,
    'src="$1file/$2"',
  )

  return processed
    .replace(/color:\s*#000000/gi, 'color: #e0e0e0')
    .replace(/color:\s*black/gi, 'color: #e0e0e0')
    .replace(/color:\s*rgb\(0,\s*0,\s*0\)/gi, 'color: #e0e0e0')
    .replace(/background-color:\s*#ffffff/gi, 'background-color: transparent')
    .replace(/background-color:\s*white/gi, 'background-color: transparent')
    .replace(/background-color:\s*rgb\(255,\s*255,\s*255\)/gi, 'background-color: transparent')
}

export function getAllowedAIIDEs(homework) {
  const raw = homework?.allowedAIIDEs
  if (!raw) return ['trae']
  if (Array.isArray(raw)) return raw.length > 0 ? raw : ['trae']
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : ['trae']
  } catch {
    return [raw]
  }
}

export function getAIIDELabel(ide) {
  return ({ trae: 'Trae', youlaber: 'YouLaber' })[ide] || ide
}

